import type { ParserBlock } from '@vif/parsers';
import { assertPersistedBlocksBound } from './backup-limits.js';
import type { VifDB } from './db.js';
import type { ContentIdentity } from './hash.js';
import type {
  BlockRow,
  BookmarkRow,
  DocumentFormat,
  PositionRow,
  StoredDocument,
} from './types.js';

/**
 * W-1 (Sol M-01, web half) — the commit-boundary admission transaction, the
 * web counterpart of mobile's storage/admission.ts. The final import commit is
 * ONE IndexedDB readwrite transaction spanning every involved store:
 *
 *  - PD-9 identity re-check INSIDE the transaction (the pre-parse lookup in
 *    import-document is a fast-fail only);
 *  - slot COUNT vs the D-4 cap inside the same transaction — never screen
 *    state, so concurrent imports serialize at commit and exactly one wins
 *    at 4/5 (IndexedDB serializes overlapping readwrite transactions on the
 *    same stores);
 *  - document + blocks + §6.6 legacy-resume claim commit atomically or not
 *    at all.
 *
 * Every entry path (file import, paste) routes through this; disabling the
 * import controls during an active import is UX polish, not the enforcement.
 *
 * W-1 (reopened) — TRANSACTIONAL INTEGRITY UNDER FAILURE. IndexedDB auto-commits
 * a readwrite transaction the moment its request queue drains and control
 * returns to the event loop. So a synchronous throw AFTER writes are queued but
 * before `tx.done` would let the queued writes COMMIT while the error
 * propagates — a partial commit. Two defences:
 *  1. HOIST every throw-capable NON-IDB step (the block-row shaping, the W-4
 *     byte invariant) to BEFORE the transaction opens. Inside the txn body only
 *     IDB requests are issued and awaited — nothing else can throw. Auto-commit
 *     stays satisfied (no non-IDB awaits inside), exactly as before.
 *  2. BELT-AND-BRACES abort guard for the one throw source that CANNOT be
 *     hoisted (legacy shaping reads the legacy row inside the txn, for atomic
 *     claim): the whole txn body is wrapped so that EVERY unsuccessful exit —
 *     thrown fault, duplicate, cap-full — explicitly aborts the txn. Uniform
 *     rule: no unsuccessful path lets the readwrite txn commit, even a
 *     zero-write one.
 */

export interface AdmitImportInput {
  readonly title: string;
  readonly format: DocumentFormat;
  readonly lang: string;
  readonly blocks: readonly ParserBlock[];
  readonly wordCount: number;
  /** PD-9 identity, computed from the raw bytes before parsing. */
  readonly identity: ContentIdentity;
  /** D-4 cap: admission fails with 'library-full' at or past this count. */
  readonly maxDocuments: number;
  /** §6.6 legacy identities to claim (position + bookmarks re-key), atomically. */
  readonly legacyHashes?: readonly string[];
  readonly now?: number;
}

export type AdmitOutcome =
  | { readonly status: 'added'; readonly doc: StoredDocument; readonly legacyResumed: boolean }
  | { readonly status: 'duplicate'; readonly doc: StoredDocument }
  | { readonly status: 'library-full'; readonly limit: number };

export async function admitAndCommitImport(
  db: VifDB,
  input: AdmitImportInput,
): Promise<AdmitOutcome> {
  const { contentHash, size } = input.identity;
  const now = input.now ?? Date.now();
  const document: StoredDocument = {
    id: crypto.randomUUID(),
    title: input.title,
    format: input.format,
    lang: input.lang,
    wordCount: input.wordCount,
    addedAt: now,
    contentHash,
    size,
  };

  // W-1 defence #1: shape the block rows BEFORE the transaction. This is the
  // one throw-capable step that we can hoist (the legacy shaping cannot — it
  // reads the legacy row inside the txn for an atomic claim).
  const blockRows: BlockRow[] = input.blocks.map((block, idx) => ({
    docId: document.id,
    idx,
    kind: block.kind,
    level: block.level ?? null,
    text: block.text,
  }));

  // W-4 import invariant, also BEFORE the transaction: bound the persisted
  // document so its serialized blocks entry provably fits the restore caps.
  // A racing re-import that slips past the pre-parse duplicate fast-fail is
  // vanishingly rare, so running this ahead of the in-txn duplicate/cap check
  // is acceptable and keeps all throw-capable work out of the txn body.
  assertPersistedBlocksBound(blockRows);

  const tx = db.transaction(
    ['documents', 'blocks', 'positions', 'bookmarks', 'legacyResume'],
    'readwrite',
  );

  // Fire-and-forget a write request. On commit its promise resolves; on abort
  // idb REJECTS every queued write promise, so we swallow that expected
  // rejection here (tx.done is the single source of truth for commit success) —
  // otherwise an aborted put surfaces as an unhandled AbortError.
  const queue = (request: Promise<unknown>): void => {
    void request.catch(() => undefined);
  };

  // W-1 defence #2: the belt-and-braces abort guard. Aborting an
  // already-settled txn throws InvalidStateError — swallow it. The tx.done
  // rejection an abort triggers is expected — swallow that too. Every
  // unsuccessful exit routes through here so the readwrite txn never commits.
  const abortAndSettle = async (): Promise<void> => {
    try {
      tx.abort();
    } catch {
      // Already committed/aborted — nothing left to roll back.
    }
    try {
      await tx.done;
    } catch {
      // The expected AbortError (or a genuine commit error we are discarding on
      // an already-failing path).
    }
  };

  try {
    const documents = tx.objectStore('documents');

    // Authority checks INSIDE the transaction. Duplicate first: re-importing
    // existing content is never blocked by a full library (PD-9 returns the
    // existing entry; the cap only gates ADDING).
    const candidates = await documents.index('by-contentHash').getAll(contentHash);
    const existing = candidates.find((doc) => doc.size === size);
    if (existing !== undefined) {
      await abortAndSettle();
      return { status: 'duplicate', doc: existing };
    }
    const count = await documents.count();
    if (count >= input.maxDocuments) {
      await abortAndSettle();
      return { status: 'library-full', limit: input.maxDocuments };
    }

    queue(documents.put(document));
    const blocks = tx.objectStore('blocks');
    for (const row of blockRows) queue(blocks.put(row));

    // §6.6 legacy-resume claim, inside the SAME transaction: first matching
    // legacy identity wins, its position/bookmarks are re-keyed onto the new
    // document, and the legacy row is consumed. (Mirrors storage/legacy.ts
    // claimLegacyResume — keep in lock-step.) The shaping here reads the legacy
    // row inside the txn, so a malformed persisted value can throw AFTER the
    // document/blocks writes are queued — the abort guard rolls them back.
    let legacyResumed = false;
    const legacyStore = tx.objectStore('legacyResume');
    for (const hash of input.legacyHashes ?? []) {
      const legacy = await legacyStore.get(hash);
      if (legacy === undefined) continue;
      if (typeof legacy.lastPosition === 'number' && legacy.lastPosition > 0) {
        const position: PositionRow = {
          docId: document.id,
          wordIdx: legacy.lastPosition,
          updatedAt: now,
        };
        queue(tx.objectStore('positions').put(position));
      }
      for (const wordIdx of legacy.bookmarks) {
        const bookmark: BookmarkRow = { docId: document.id, wordIdx, label: '', createdAt: now };
        queue(tx.objectStore('bookmarks').put(bookmark));
      }
      queue(legacyStore.delete(hash));
      legacyResumed = true;
      break;
    }

    await tx.done;
    return { status: 'added', doc: document, legacyResumed };
  } catch (error) {
    // Any fault after writes are queued: abort so nothing partial commits, then
    // re-surface the original error.
    await abortAndSettle();
    throw error;
  }
}
